"use client";

import { RiRainbowLine } from "@remixicon/react";
import { cn } from "@workspace/ui/lib/utils";
import Image from "next/image";
import { memo, useCallback, useState } from "react";

export interface FestivalPosterProps {
	name: string;
	posterUrl?: string | null;
	size?: "sm" | "md" | "lg" | "xl" | "full";
	priority?: boolean;
	fetchPriority?: "high" | "low" | "auto";
	loading?: "eager" | "lazy";
	className?: string;
	rounded?: "sm" | "md" | "lg" | "xl" | undefined;
	onLoad?: () => void;
	onError?: (error: Error) => void;
	objectPosition?:
		| "center"
		| "top"
		| "bottom"
		| "left"
		| "right"
		| "center top"
		| "center bottom";
	objectFit?: "cover" | "contain" | "smart";
}

const sizeClasses = {
	sm: "w-16 h-[5.33rem]", // 3:4 ratio
	md: "w-24 h-32", // 3:4 ratio
	lg: "w-32 h-[10.67rem]", // 3:4 ratio
	xl: "w-48 h-64", // 3:4 ratio
	full: "w-full aspect-[3/4]",
};

const roundedClasses = {
	sm: "rounded-sm",
	md: "rounded-md",
	lg: "rounded-lg",
	xl: "rounded-xl",
};

const iconSizes = {
	sm: 16,
	md: 20,
	lg: 24,
	xl: 32,
	full: 48,
};

export const FestivalPoster = memo<FestivalPosterProps>(
	({
		name,
		posterUrl,
		size = "lg",
		priority = false,
		fetchPriority = "auto",
		loading = "lazy",
		className = "",
		rounded = "lg",
		onLoad,
		onError,
		objectPosition = "center",
		objectFit = "smart",
	}) => {
		const [imageError, setImageError] = useState(false);
		const [imageLoaded, setImageLoaded] = useState(false);

		const handleImageError = useCallback(() => {
			setImageError(true);
			const error = new Error(`Failed to load poster for ${name}`);
			onError?.(error);
		}, [name, onError]);

		const handleImageLoad = useCallback(() => {
			setImageLoaded(true);
			onLoad?.();
		}, [onLoad]);

		const sizeClass = sizeClasses[size];
		const iconSize = iconSizes[size];
		const roundedClass = rounded ? roundedClasses[rounded] : "";

		// Smart object-fit: always use cover for proper cropping
		const getObjectFit = () => {
			if (objectFit === "smart") {
				return "cover"; // Use cover for proper cropping without letterboxing
			}
			return objectFit;
		};

		return (
			<div
				className={cn(
					"relative overflow-hidden bg-muted",
					roundedClass,
					sizeClass,
					className,
				)}
			>
				{posterUrl && !imageError ? (
					<>
						<Image
							src={posterUrl}
							alt={`${name} festival poster`}
							fill
							sizes={
								size === "full"
									? "(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
									: size === "xl"
										? "192px"
										: size === "lg"
											? "128px"
											: size === "md"
												? "96px"
												: "64px"
							}
							className={cn(
								"transition-opacity duration-200",
								imageLoaded ? "opacity-100" : "opacity-0",
							)}
							style={{
								objectFit: getObjectFit(),
								objectPosition,
							}}
							priority={priority}
							fetchPriority={fetchPriority}
							{...(priority ? {} : { loading })}
							onError={handleImageError}
							onLoad={handleImageLoad}
							placeholder="empty"
						/>

						{/* Loading placeholder with blurred skeleton effect */}
						{!imageLoaded && (
							<div className="absolute inset-0 bg-gradient-to-b from-muted/80 to-muted backdrop-blur-sm" />
						)}
					</>
				) : (
					/* Fallback icon */
					<div className="flex h-full w-full items-center justify-center">
						<RiRainbowLine size={iconSize} className="text-muted-foreground" />
					</div>
				)}
			</div>
		);
	},
);

FestivalPoster.displayName = "FestivalPoster";
